'use client';

import React from 'react';
import Link from 'next/link';
import { NavigationItemWithRoute } from '@/types/types';

interface MobileMainNavProps {
  navItems: NavigationItemWithRoute[];
  contactLinks: ContactLinks;
  iconsData: { [key: string]: React.ReactNode };
}

import { Menu } from 'lucide-react';
// import Image from 'next/image';
import { ContactLinks } from '@/types/types'; // Import the NavLinks type
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/app/shared/ui/accordion';
import { Button } from '@/app/shared/ui/button';

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/app/shared/ui/sheet';
import { cn } from '@/lib/utils';
import Logo from '@/app/shared/common/Logo';
import FooterLogo from '@/app/layout/footer/FooterLogo';
import FooterSocialLinks from '@/app/layout/footer/FooterSocialLinks';
import FooterCopyright from '@/app/layout/footer/FooterCopyright';
// import logger from '@/lib/logger';

/**
 * MobileMainNav component renders the mobile version of the main navigation.
 * It displays a navigation menu with items and submenus, and includes a button to trigger a sheet
 * containing the navigation links.
 *
 * @component
 * @param {MobileMainNavProps} props - The props for the MobileMainNav component.
 * @param {Array} props.navItems - An array of navigation items to be displayed in the menu.
 * Each item can have a text and a URL, and optionally a submenu with additional items.
 *
 * @example
 * const navItems = [
 *   { text: 'Home', url: '/' },
 *   { text: 'About', url: '/about', submenu: [{ text: 'Team', url: '/about/team' }] },
 * ];
 *
 * <MobileMainNav navItems={navItems} />
 */
const MobileMainNav: React.FC<MobileMainNavProps> = ({ navItems }) => {
  return (
    <>
      {/* Mobile Navigation */}
      <div className='block lg:hidden'>
        <div className='flex items-center justify-between '>
          <div className='flex items-center pt-2 pb-2 '>
            <Logo />
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant={'outline'} size={'icon'}>
                <Menu className='size-4' />
              </Button>
            </SheetTrigger>
            <SheetContent className='overflow-y-auto bg-designer-black text-designer-link-gray'>
              <SheetHeader>
                <SheetTitle>
                  <div className='flex items-center pl-4'>
                    <SheetClose asChild>
                      <Logo />
                    </SheetClose>
                  </div>
                </SheetTitle>
              </SheetHeader>

              <div className='my-8 flex flex-col '>
                {navItems.map((navItem, idx) =>
                  navItem.submenu && navItem.submenu.length > 0 ? (
                    <Accordion
                      key={idx}
                      type='single'
                      collapsible
                      className='w-full'
                    >
                      <AccordionItem
                        value={navItem.text}
                        className='border-b-0 hover:text-foreground'
                      >
                        <AccordionTrigger className='py-0 font-semibold hover:no-underline text-designer-link-gray text-lg hover:text-foreground'>
                          {navItem.text}
                        </AccordionTrigger>
                        <AccordionContent className='mt-2 pb-2'>
                          {navItem.submenu.map((subItem, subIdx) => (
                            <SheetClose asChild key={subIdx}>
                              <Link
                                className={cn(
                                  'flex select-none gap-4 rounded-md p-3 leading-none outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground'
                                )}
                                href={subItem.url}
                              >
                                <div>
                                  <div className='text-sm font-semibold text-designer-link-gray'>
                                    {subItem.text}
                                  </div>
                                </div>
                              </Link>
                            </SheetClose>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ) : (
                    <SheetClose
                      asChild
                      key={idx}
                      className='border-designer-link-gray border-b pb-4'
                    >
                      {/* Menu Links without sub items. will close menu  */}
                      <Link
                        href={navItem.url}
                        className='text-designer-link-gray text-lg pl-4  first:border-t border-designer-link-gray py-4 hover:bg-gray-100 hover:text-gray-900'
                      >
                        {navItem.text}
                      </Link>
                    </SheetClose>
                  )
                )}
              </div>

              {/* Contact Links */}
              {/* <div > */}
              <div className='mx-auto max-w-screen-2xl px-4 pb-6 pt-6 sm:px-6 lg:px-8 lg:pt-8'>
                <div className='grid grid-cols-1 gap-8 justify-center items-center'>
                  <div>
                    <FooterLogo />
                    <FooterSocialLinks />
                  </div>
                  {/* <FooterLinks /> */}
                </div>
                <FooterCopyright />
              </div>
              {/* <div className='grid grid-cols-2 justify-start'>
                  <Link
                    className={cn(
                      buttonVariants({
                        variant: 'ghost',
                      }),
                      'justify-start text-muted-foreground'
                    )}
                    href='#'
                  >
                    Press
                  </Link>
                  <Link
                    className={cn(
                      buttonVariants({
                        variant: 'ghost',
                      }),
                      'justify-start text-muted-foreground'
                    )}
                    href='#'
                  >
                    Contact
                  </Link>
                  <Link
                    className={cn(
                      buttonVariants({
                        variant: 'ghost',
                      }),
                      'justify-start text-muted-foreground'
                    )}
                    href='#'
                  >
                    Imprint
                  </Link>
                  <Link
                    className={cn(
                      buttonVariants({
                        variant: 'ghost',
                      }),
                      'justify-start text-muted-foreground'
                    )}
                    href='#'
                  >
                    Sitemap
                  </Link>
                  <Link
                    className={cn(
                      buttonVariants({
                        variant: 'ghost',
                      }),
                      'justify-start text-muted-foreground'
                    )}
                    href='#'
                  >
                    Legal
                  </Link>
                  <Link
                    className={cn(
                      buttonVariants({
                        variant: 'ghost',
                      }),
                      'justify-start text-muted-foreground'
                    )}
                    href='#'
                  >
                    Cookie Settings
                  </Link>
                </div>
                <div className='mt-2 flex flex-col gap-3'>
                  
                </div> */}
              {/* </div> */}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </>
  );
};

export default MobileMainNav;
